import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthCard from '../components/AuthCard';
import { authStyles } from '../styles/authStyles';

export default function ConfirmEmailChangePage() {
  const [searchParams] = useSearchParams();
  const { fetchMe } = useAuth();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [newEmail, setNewEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  // The token is single-use, so confirm only once even if the effect re-runs
  // (StrictMode runs it twice in dev; a fetchMe identity change could too).
  const confirmCalledRef = useRef(false);

  useEffect(() => {
    if (confirmCalledRef.current) return;
    confirmCalledRef.current = true;
    if (!token) {
      setStatus('error');
      setErrorMsg('This confirmation link is missing or malformed.');
      return;
    }

    async function confirm() {
      try {
        const res = await fetch('/api/teacher/confirm-email-change', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setNewEmail(data.new_email || '');
        // Refresh session so AuthContext reflects the new email immediately
        await fetchMe().catch(() => {});
        setStatus('success');
      } catch (err) {
        setErrorMsg(err.message);
        setStatus('error');
      }
    }

    confirm();
  }, [token, fetchMe]);

  return (
    <AuthCard>
      {status === 'loading' && (
        <>
          <h1 style={styles.heading}>Confirming…</h1>
          <p style={styles.sub}>Please wait while we update your email address.</p>
        </>
      )}

      {status === 'success' && (
        <>
          <h1 style={styles.heading}>Email updated</h1>
          <p style={styles.sub}>
            Your login email has been changed{newEmail ? ` to ${newEmail}` : ''}.
            Use it the next time you sign in.
          </p>
          <Link to="/account" style={styles.link}>Back to account settings</Link>
        </>
      )}

      {status === 'error' && (
        <>
          <h1 style={styles.heading}>Link invalid or expired</h1>
          <p style={styles.sub}>{errorMsg}</p>
          <Link to="/account" style={styles.link}>Go to account settings</Link>
        </>
      )}
    </AuthCard>
  );
}

// Page-specific overrides on top of ../styles/authStyles
const styles = {
  heading: { ...authStyles.heading, margin: '0 0 8px' },
  sub: { ...authStyles.sub, margin: '0 0 20px', lineHeight: '1.6' },
  link: { ...authStyles.link, fontSize: '14px' },
};
